const LOGO_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'svg']);

export const LOGO_MAX_BYTES = 1000000;

const rawLogoFile = file => file?.rawFile || file;

export const isAllowedLogoFile = file => {
    if (!file) {
        return true;
    }

    const name = rawLogoFile(file)?.name || file?.title || '';
    const extension = name.includes('.') ? name.split('.').pop().toLowerCase() : '';
    return LOGO_EXTENSIONS.has(extension);
};

export const optionalDigits = message => value => {
    if (value === undefined || value === null || value === '') {
        return undefined;
    }

    return /^\d+$/.test(String(value)) ? undefined : message;
};

export const optionalCurrency = message => value => {
    if (value === undefined || value === null || value === '') {
        return undefined;
    }

    if (!/^\d+(,\d{1,2})?$/.test(String(value))) {
        return message;
    }

    const amount = Number(String(value).replace(',', '.'));
    if (!Number.isFinite(amount) || amount < 0 || amount > 100) {
        return message;
    }

    return undefined;
};

export const parseOptionalInteger = value => {
    if (value === undefined || value === null || value === '') {
        return null;
    }

    return Number(value);
};

export const parseOptionalCurrency = value => {
    if (value === undefined || value === null || value === '') {
        return null;
    }

    return Number(String(value).replace(',', '.'));
};

const DISCOUNT_LEVELS = [1, 2, 3, 4, 5, 6];

export const formatOptionalNumberInput = value => {
    if (value === null || value === undefined || value === '') {
        return '';
    }

    const amount = Number(value);
    if (!Number.isFinite(amount)) {
        return '';
    }

    return String(amount).replace('.', ',');
};

export const formatOptionalIntegerInput = value => {
    if (value === null || value === undefined || value === '') {
        return '';
    }

    return String(value);
};

export const toRetailChainFormValues = record => {
    if (!record) {
        return record;
    }

    const values = {
        ...record,
        logo: null,
        description: record.description ?? '',
        defaultDepositValue: formatOptionalNumberInput(record.defaultDepositValue),
        defaultDepositPoints: formatOptionalIntegerInput(record.defaultDepositPoints),
    };

    DISCOUNT_LEVELS.forEach(level => {
        values[`discountLimitL${level}`] = formatOptionalIntegerInput(record[`discountLimitL${level}`]);
        values[`discountValueL${level}`] = formatOptionalIntegerInput(record[`discountValueL${level}`]);
    });

    return values;
};

const payloadFields = [
    'name',
    'forwarderType',
    'operators',
    'couponSource',
    'depositWithdrawalMethods',
    'serviceCompanyIds',
    'defaultServiceCompanyId',
    'description',
    'defaultDepositValue',
    'defaultDepositPoints',
    'logo',
    ...DISCOUNT_LEVELS.flatMap(level => [`discountLimitL${level}`, `discountValueL${level}`]),
];

export const transformRetailChainPayload = data => {
    const payload = {};
    payloadFields.forEach(field => {
        if (Object.hasOwn(data, field)) {
            payload[field] = data[field];
        }
    });
    if (data.id) {
        payload.id = data.id;
    }

    const logo = payload.logo;
    payload.logo = logo && typeof logo === 'object' && logo.rawFile instanceof File ? logo : null;
    payload.description = payload.description ? payload.description : null;
    payload.defaultDepositValue = parseOptionalCurrency(payload.defaultDepositValue);
    payload.defaultDepositPoints = parseOptionalInteger(payload.defaultDepositPoints);

    DISCOUNT_LEVELS.forEach(level => {
        payload[`discountLimitL${level}`] = parseOptionalInteger(payload[`discountLimitL${level}`]);
        payload[`discountValueL${level}`] = parseOptionalInteger(payload[`discountValueL${level}`]);
    });

    const methods = Array.isArray(payload.depositWithdrawalMethods) ? payload.depositWithdrawalMethods : [];
    payload.depositWithdrawalMethods = methods.includes('cash') ? methods : ['cash', ...methods];

    return payload;
};
